import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BackButton, Button, Card, ScrollRegion, Text } from "@andrum/ui";
import type { ExperimentCategory } from "@andrum/schemas";
import {
  CategoryScienceCard,
  MethodOverviewCard,
} from "@/features/learn/components/SciencePanel";
import { FeedbackBar } from "@/features/learn/components/FeedbackBar";
import {
  fetchInsightTrack,
  markInsightOpen,
  type DailyInsight,
} from "@/features/learn/api/insights-api";
import { useExperimentStore } from "@/stores/experiment-store";

const CATEGORIES: ExperimentCategory[] = [
  "recovery",
  "presence",
  "connection",
  "gratitude",
  "movement",
  "meaning",
];

type Mode = "menu" | "method" | "categories" | "track" | "detail";

export function LearnScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const assignFromCatalog = useExperimentStore((s) => s.assignFromCatalog);
  const experiments = useExperimentStore((s) => s.experiments);
  const [mode, setMode] = useState<Mode>("menu");
  const [catIndex, setCatIndex] = useState(0);
  const [track, setTrack] = useState<DailyInsight[]>([]);
  const [detail, setDetail] = useState<DailyInsight | null>(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const [tryBusy, setTryBusy] = useState(false);

  useEffect(() => {
    if (mode !== "track" && mode !== "detail") return;
    let cancelled = false;
    void (async () => {
      setTrackLoading(true);
      const res = await fetchInsightTrack();
      if (cancelled) return;
      if (res) {
        setTrack(res.items);
      }
      setTrackLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const totalCats = 1 + CATEGORIES.length;
  const category =
    catIndex === 0
      ? null
      : CATEGORIES[Math.min(catIndex - 1, CATEGORIES.length - 1)];

  const tryLinked = async (slug: string | null) => {
    if (!slug) {
      void navigate({ to: "/app/explore" });
      return;
    }
    const match = experiments.find((e) => e.slug === slug);
    if (!match) {
      void navigate({ to: "/app/explore" });
      return;
    }
    setTryBusy(true);
    const next = await assignFromCatalog(match.id);
    setTryBusy(false);
    if (!next) {
      void navigate({ to: "/app/explore" });
      return;
    }
    void navigate({
      to: "/experiment/$assignmentId",
      params: { assignmentId: next.id },
    });
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background px-5 pt-2 pb-3 sm:px-6">
      <BackButton
        className="mb-1 shrink-0"
        onClick={() => {
          if (mode === "menu") void navigate({ to: "/app/insights" });
          else if (mode === "detail") {
            setDetail(null);
            setMode("track");
          } else setMode("menu");
        }}
      >
        {t("learn.back")}
      </BackButton>

      <header className="shrink-0 space-y-1 pb-2">
        <Text variant="title" as="h1">
          {mode === "track" || mode === "detail"
            ? t("dailyInsight.trackTitle")
            : t("learn.title")}
        </Text>
        <Text variant="muted" className="text-sm leading-snug">
          {mode === "track"
            ? t("dailyInsight.trackLead")
            : mode === "detail"
              ? t("dailyInsight.trackLead")
              : t("learn.lead")}
        </Text>
      </header>

      <ScrollRegion>
        <div className="space-y-3 pb-6">
          {mode === "menu" ? (
            <>
              <button
                type="button"
                onClick={() => setMode("track")}
                className="w-full rounded-2xl border border-border bg-surface-raised px-4 py-3 text-left"
              >
                <Text variant="label">{t("dailyInsight.trackTitle")}</Text>
                <Text variant="subtle" className="text-sm leading-snug">
                  {t("dailyInsight.trackLead")}
                </Text>
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode("method");
                  setCatIndex(0);
                }}
                className="w-full rounded-2xl border border-border bg-surface-raised px-4 py-3 text-left"
              >
                <Text variant="label">{t("learn.overviewTitle")}</Text>
                <Text variant="subtle" className="text-sm leading-snug">
                  {t("learn.methodCardBody")}
                </Text>
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode("categories");
                  setCatIndex(1);
                }}
                className="w-full rounded-2xl border border-border bg-surface-raised px-4 py-3 text-left"
              >
                <Text variant="label">{t("learn.tracksTitle")}</Text>
                <Text variant="subtle" className="text-sm leading-snug">
                  {t("learn.tracksLead")}
                </Text>
              </button>
            </>
          ) : null}

          {mode === "method" ? (
            <>
              <MethodOverviewCard />
              <Card className="space-y-2 p-4">
                <Text variant="label">{t("learn.loopLabel")}</Text>
                <Text variant="body" className="text-sm leading-relaxed">
                  {t("learn.loopBody")}
                </Text>
              </Card>
            </>
          ) : null}

          {mode === "categories" ? (
            category ? (
              <CategoryScienceCard category={category} defaultOpen />
            ) : (
              <MethodOverviewCard />
            )
          ) : null}

          {mode === "track" ? (
            trackLoading ? (
              <Text variant="muted">{t("dailyInsight.loading")}</Text>
            ) : track.length === 0 ? (
              <div className="space-y-2">
                <Text variant="muted" className="text-sm leading-relaxed">
                  {t("dailyInsight.trackEmpty")}
                </Text>
                <Text variant="subtle" className="text-sm leading-relaxed">
                  {t("dailyInsight.trackEmptyHint")}
                </Text>
              </div>
            ) : (
              track.map((item) => {
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setDetail(item);
                      setMode("detail");
                      void markInsightOpen(item.id, true);
                    }}
                    className="w-full rounded-2xl border border-border bg-surface-raised px-3 py-2.5 text-left"
                  >
                    <Text
                      variant="body"
                      className="text-sm font-medium leading-snug"
                    >
                      {item.title}
                    </Text>
                    <Text
                      variant="subtle"
                      className="mt-1 line-clamp-2 text-xs leading-snug"
                    >
                      {item.takeaway}
                    </Text>
                  </button>
                );
              })
            )
          ) : null}

          {mode === "detail" && detail ? (
            <Card className="space-y-3 p-4">
              <Text variant="title" as="h2" className="text-lg">
                {detail.title}
              </Text>
              <Text variant="body" className="leading-relaxed">
                {detail.body}
              </Text>
              {detail.takeaway ? (
                <div className="rounded-xl border border-border/80 bg-surface-muted/50 px-3 py-2.5">
                  <Text variant="caption" className="font-medium">
                    {t("dailyInsight.tryToday")}
                  </Text>
                  <Text variant="body" className="mt-1 text-sm leading-relaxed">
                    {detail.takeaway}
                  </Text>
                </div>
              ) : null}
              <FeedbackBar source="insight" sourceId={detail.id} />
              <Button
                fullWidth
                variant="secondary"
                loading={tryBusy}
                onClick={() =>
                  void tryLinked(detail.suggestedExperimentSlug)
                }
              >
                {t("dailyInsight.tryStep")}
              </Button>
            </Card>
          ) : null}
        </div>
      </ScrollRegion>

      {mode === "categories" || mode === "method" ? (
        <div className="flex shrink-0 gap-2 border-t border-border pt-3">
          <Button
            fullWidth
            variant="ghost"
            className="min-h-11"
            disabled={mode === "method" || catIndex <= 0}
            onClick={() => {
              if (mode === "categories" && catIndex <= 1) {
                setMode("method");
                setCatIndex(0);
              } else setCatIndex((i) => Math.max(0, i - 1));
            }}
          >
            {t("back")}
          </Button>
          <Button
            fullWidth
            variant="soft"
            className="min-h-11"
            disabled={mode === "categories" && catIndex >= totalCats - 1}
            onClick={() => {
              if (mode === "method") {
                setMode("categories");
                setCatIndex(1);
              } else setCatIndex((i) => Math.min(totalCats - 1, i + 1));
            }}
          >
            {t("next")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
